import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { SiteFooter, SiteHeader } from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const btn = "inline-flex h-12 items-center justify-center gap-2 rounded-lg px-6 text-base font-semibold transition-[background-color] duration-200 disabled:opacity-60";

const LoggedOut = ({ info, token }) => {
  const q = `?next=${encodeURIComponent(`/cohost/accept?token=${token}`)}&email=${encodeURIComponent(info.invited_email)}`;
  return (
    <div className="mt-8 space-y-3">
      <p className="text-sm text-slate-600">Log in or create a free account to accept. The invite was sent to <strong data-testid="cohost-accept-invited-email">{info.invited_email}</strong>.</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Link to={`/login${q}`} data-testid="cohost-accept-login-btn" className={`${btn} bg-blue-700 text-white hover:bg-blue-600 hover:text-white`}>Log in</Link>
        <Link to={`/signup${q}`} data-testid="cohost-accept-signup-btn" className={`${btn} border border-slate-200 bg-white text-slate-800 hover:bg-slate-50`}>Create account</Link>
      </div>
    </div>
  );
};

const LoggedIn = ({ info, token, user, logout, onDeclined }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const differs = user.email.toLowerCase() !== info.invited_email.toLowerCase();
  const act = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/cohost-invites/${kind}`, { token });
      if (kind === "decline") return onDeclined();
      toast.success(`You're now a co-host for ${info.occasion}`);
      navigate(`/events/${data.event_id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="mt-8 space-y-4">
      <p className="text-sm text-slate-600">Logged in as <strong data-testid="cohost-accept-current-email">{user.email}</strong>. <button data-testid="cohost-accept-switch-btn" onClick={logout} className="font-semibold text-blue-700 underline-offset-2 hover:underline">Not you?</button></p>
      {differs && (
        <p data-testid="cohost-accept-email-note" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This invite was sent to {info.invited_email}. If you accept, {user.email} will become the co-host.
        </p>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button data-testid="cohost-accept-btn" disabled={!!busy} onClick={() => act("accept")} className={`${btn} bg-blue-700 text-white hover:bg-blue-600`}>
          {busy === "accept" ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-5 w-5" />} Accept
        </button>
        <button data-testid="cohost-decline-btn" disabled={!!busy} onClick={() => act("decline")} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>Decline</button>
      </div>
    </div>
  );
};

export default function CohostAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (!token) return setError("This invite link is missing its code. Open the link from your email again.");
    api.post("/cohost-invites/lookup", { token }).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  let body;
  if (error) body = <p data-testid="cohost-accept-error" className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>;
  else if (declined) body = <p data-testid="cohost-accept-declined" className="mt-6 text-slate-600">You declined this invite. You can close this page.</p>;
  else if (!info || user === null) body = <Loader2 className="mt-8 h-6 w-6 animate-spin text-blue-700" />;
  else if (!user) body = <LoggedOut info={info} token={token} />;
  else body = <LoggedIn info={info} token={token} user={user} logout={logout} onDeclined={() => setDeclined(true)} />;

  return (
    <div className="confetti-bg-white font-manrope flex min-h-screen flex-col text-slate-900">
      <SiteHeader>
        <Link to="/" data-testid="cohost-accept-home-link" className="text-sm font-medium text-slate-600 hover:text-blue-700">Home</Link>
      </SiteHeader>
      <div className="flex flex-1 items-start justify-center px-4 pb-16 pt-10 sm:items-center">
        <div data-testid="cohost-accept-card" className="rise w-full max-w-md rounded-2xl border border-slate-200 bg-white/90 p-8 shadow-sm backdrop-blur-xl sm:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-700">Co-host invite</p>
          <h1 data-testid="cohost-accept-title" className="font-display mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {info ? `${info.owner_name} invited you to co-host ${info.occasion}` : "Co-host invite"}
          </h1>
          {info && !error && !declined && <p className="mt-3 text-sm text-slate-500">Co-hosts can edit the invite, see responses, manage the guest list and check guests in.</p>}
          {body}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
